import { applyInferredTypes, ensureDefaultSubType } from '@/utils/routineTypeInfer'
import {
  inferRoutinePresetKey,
  normalizeFormFreqValue,
  parseRoutineSchedule,
  scheduleHasMonth
} from '@/utils/routineFreq'

/** 判断字符串是否像「监测项目」列表（非点位名称） */
export function looksLikeItemNames(text) {
  const t = String(text || '').trim()
  if (!t) return false
  if (/(排放口|断面|出口|进口|处理站|污水站|废水站|监测井|采样点|水井|井$)/.test(t) && t.length < 48) return false
  const parts = t.split(/[、,，;；/\n]/).map(s => s.trim()).filter(Boolean)
  if (parts.length >= 2) return true
  if (/[、,，;；]/.test(t)) return true
  if (/(总[汞镉铬砷铅镍银铜锌铍铁锰])|(PH|pH|SS|TDS|氨氮|硝酸盐|亚硝酸盐|大肠菌群|苯并芘|铊|浑浊度|硫酸盐|硬度|水位)/i.test(t)) return true
  if (/新增[:：]/.test(t)) return true
  return t.length > 24
}

/** 点位库 → 监测条目字段 */
export function mapSiteToMonitorFields(site) {
  const s = site || {}
  const region = String(s.region_name || '').trim()
  const siteName = String(s.site_name || '').trim()
  const siteCode = String(s.site_code || '').trim()
  let itemNames = String(s.item_names || '').trim()
  let monitorPlace = ''

  if (looksLikeItemNames(siteName)) {
    if (!itemNames) itemNames = siteName
    monitorPlace = [region, siteCode].filter(Boolean).join(' · ')
    if (!monitorPlace) monitorPlace = '待填写点位'
  } else if (!itemNames && looksLikeItemNames(region)) {
    itemNames = region
    monitorPlace = siteName || siteCode || '待填写点位'
  } else {
    monitorPlace = [region, siteName].filter(Boolean).join(region && siteName ? ' · ' : '')
    if (!monitorPlace) monitorPlace = siteName || '待填写点位'
  }

  return { monitor_place: monitorPlace, item_names: itemNames }
}

function resolveTypeCode(form, typeConfig) {
  if (form.type_code || !form.sub_type || !typeConfig || !typeConfig.basicTypes) return
  const b = typeConfig.basicTypes.find(x => x.label === form.basic_type)
  const st = b && (b.subTypes || []).find(x => x.label === form.sub_type)
  if (st) form.type_code = st.code || ''
}

function applyRoutineFreqFromSite(item, site, freqPresets) {
  if (site.freq_count) item.freq_count = parseInt(site.freq_count, 10) || 1
  if (site.periods_count) item.periods_count = parseInt(site.periods_count, 10) || 1
  let schedule = parseRoutineSchedule(site.schedule_json)
  const presetKey = inferRoutinePresetKey({
    freq_label: site.freq_label,
    freq_count: item.freq_count,
    periods_count: item.periods_count,
    schedule_json: site.schedule_json
  }, freqPresets)
  if (presetKey && freqPresets[presetKey]) {
    item.freq_label = presetKey
    const p = freqPresets[presetKey]
    if (p.freq_count) item.freq_count = p.freq_count
    if (p.periods_count) item.periods_count = p.periods_count
    if (p.schedule) schedule = parseRoutineSchedule(p.schedule)
  } else {
    if (site.schedule_json && scheduleHasMonth(schedule)) {
      item.freq_label = normalizeFormFreqValue(site.freq_label, freqPresets)
      if (!item.freq_label) item.freq_label = ''
    } else {
      item.freq_label = normalizeFormFreqValue(site.freq_label, freqPresets)
    }
    if (!item.freq_label && scheduleHasMonth(schedule)) {
      item.freq_label = ''
    }
  }
  if (item.freq_label === '__custom__') item.freq_label = ''
  item.freq_unit = 'period'
  item.freq_per_day = item.freq_count || 1
  item.schedule_json = JSON.stringify(schedule)
}

/**
 * 企业点位库 → 监测详细信息行（与 MonitorItemDialog.applySite 一致）
 * @param {object} site
 * @param {{ typeInferConfig?, typeConfig?, freqPresets?, isRoutine? }} opts
 */
export function siteToMonitorItem(site, opts = {}) {
  const s = site || {}
  const {
    typeInferConfig = {},
    typeConfig = {},
    freqPresets = {},
    isRoutine = true
  } = opts
  const mapped = mapSiteToMonitorFields(s)
  const methodRaw = s.method_accord != null ? s.method_accord : s.method_accord_json
  const item = {
    site_id: s.id || null,
    monitor_place: mapped.monitor_place,
    item_names: mapped.item_names,
    basic_type: s.basic_type || '',
    sub_type: s.sub_type || '',
    type_code: s.type_code || '',
    item_ids: s.item_ids || '',
    place_count: 1,
    total_days: 1,
    freq_per_day: 1,
    freq_unit: isRoutine ? 'period' : 'day',
    freq_count: parseInt(s.freq_count, 10) || 1,
    periods_count: parseInt(s.periods_count, 10) || 1,
    freq_label: s.freq_label || '',
    schedule_json: s.schedule_json || '',
    method_accord_json: methodRaw != null ? String(methodRaw) : '',
    charge_by_frequency: 1,
    remark: s.remark || ''
  }
  applyInferredTypes(item, s, typeInferConfig, typeConfig)
  ensureDefaultSubType(item, typeConfig)
  resolveTypeCode(item, typeConfig)
  if (isRoutine) {
    applyRoutineFreqFromSite(item, s, freqPresets)
  } else {
    item.freq_unit = 'day'
    item.freq_count = item.freq_per_day || 1
  }
  return normalizeMonitorItemRow(item)
}

/** 纠正「项目写在 monitor_place」的条目 */
export function normalizeMonitorItemRow(row) {
  const r = row && typeof row === 'object' ? { ...row } : {}
  let names = String(r.item_names || '').trim()
  let place = String(r.monitor_place || '').trim()

  if (!names && looksLikeItemNames(place)) {
    r.item_names = place
    r.monitor_place = '待填写点位'
    return r
  }

  if (names && looksLikeItemNames(place) && !looksLikeItemNames(names)) {
    r.monitor_place = names
    r.item_names = place
    return r
  }

  if (!names && place.length > 12) {
    r.item_names = place
    r.monitor_place = '待填写点位'
  }

  return r
}

/** 列表展示：监测项目 */
export function displayItemNames(row) {
  const r = normalizeMonitorItemRow(row)
  const names = String(r.item_names || '').trim()
  if (names) return names
  const place = String(row && row.monitor_place || '').trim()
  if (looksLikeItemNames(place)) return place
  return ''
}

/** 列表展示：监测点位 */
export function displayMonitorPlace(row) {
  const r = normalizeMonitorItemRow(row)
  const place = String(r.monitor_place || '').trim()
  if (!place || place === '待填写点位') return '待填写点位'
  if (looksLikeItemNames(place)) return '待填写点位'
  return place
}
